/**
 * Parse a Google Sheets URL like:
 * https://docs.google.com/spreadsheets/d/<SPREADSHEET_ID>/edit?gid=123#gid=123
 *
 * @throws Error if not a valid Google Sheets URL
 */
export function parseGoogleSheetUrl(input: string): {
  spreadsheetId: string
  gid: number | undefined
} {
  const u = new URL(input)

  // 1) spreadsheetId: /spreadsheets/d/<ID>/
  const m = u.pathname.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/)
  if (!m) {
    throw new Error(
      'Not a valid Google Sheets URL (missing /spreadsheets/d/<id>).',
    )
  }
  const spreadsheetId = m[1]

  // 2) gid: can appear as ?gid=... and/or #gid=...
  let gidStr = u.searchParams.get('gid') || undefined
  if (!gidStr && u.hash.startsWith('#')) {
    const hash = new URLSearchParams(u.hash.slice(1)) // drop leading "#"
    gidStr = hash.get('gid') || undefined
  }

  const gid = gidStr ? Number(gidStr) : undefined
  if (gidStr && Number.isNaN(gid)) {
    throw new Error("Found a gid, but it isn't a valid number.")
  }

  return { spreadsheetId, gid }
}

/**
 * Parse a Google Sheets URL or raw spreadsheet ID.
 * Returns { spreadsheetId, gid } where spreadsheetId may be empty string if invalid.
 */
export function parseSheetIdOrUrl(input: string): {
  spreadsheetId: string
  gid: number | undefined
} {
  const docIdRegex = /^[a-zA-Z0-9_-]{25,44}$/
  try {
    return parseGoogleSheetUrl(input)
  } catch {
    if (docIdRegex.test(input)) {
      return { spreadsheetId: input, gid: undefined }
    }
    return { spreadsheetId: '', gid: undefined }
  }
}

/**
 * Extract spreadsheet ID from a Google Sheets URL or raw ID.
 * Returns null if the input is not a valid URL or ID.
 *
 * Handles various URL formats:
 * - https://docs.google.com/spreadsheets/d/<ID>/edit
 * - https://docs.google.com/spreadsheets/d/<ID>/edit#gid=0
 * - Just the ID itself
 */
export function extractSpreadsheetId(input: string): string | null {
  if (!input) return null
  const { spreadsheetId } = parseSheetIdOrUrl(input)
  return spreadsheetId || null
}

/**
 * Validate if a string is a valid Google Sheets URL or ID
 */
export function isValidSpreadsheetUrl(input: string): boolean {
  return extractSpreadsheetId(input) !== null
}
